import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

const JsMinifierTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('minify');

  const minify = () => {
    if (!input.trim()) {
      toast.error('Please enter JavaScript to minify');
      return;
    }

    const originalSize = input.length;
    
    let minified = input
      // Remove single-line comments
      .replace(/\/\/.*$/gm, '')
      // Remove multi-line comments
      .replace(/\/\*[\s\S]*?\*\//g, '')
      // Remove whitespace around operators
      .replace(/\s*([{}();,=+\-*/<>!&|:?])\s*/g, '$1')
      // Remove multiple spaces
      .replace(/\s+/g, ' ')
      // Remove newlines
      .replace(/\n/g, '')
      // Restore necessary spaces
      .replace(/(var|let|const|function|return|if|else|for|while|do|switch|case|break|continue|new|typeof|instanceof|in|of)\s*/g, '$1 ')
      .trim();

    const minifiedSize = minified.length;
    const savings = ((originalSize - minifiedSize) / originalSize * 100).toFixed(1);

    setOutput(minified);
    setStats({ originalSize, minifiedSize, savings });
    toast.success('JavaScript minified!');
  };

  const beautify = () => {
    if (!input.trim()) {
      toast.error('Please enter JavaScript to beautify');
      return;
    }

    let beautified = input
      .replace(/;/g, ';\n')
      .replace(/{/g, ' {\n')
      .replace(/}/g, '\n}\n')
      .replace(/,\s*/g, ', ')
      .replace(/\n\s*\n/g, '\n')
      .trim();

    // Add indentation
    let indentLevel = 0;
    beautified = beautified.split('\n').map(line => {
      line = line.trim();
      if (line.includes('}')) indentLevel = Math.max(0, indentLevel - 1);
      const indented = '  '.repeat(indentLevel) + line;
      if (line.includes('{')) indentLevel++;
      return indented;
    }).join('\n');

    setOutput(beautified);
    setStats(null);
    toast.success('JavaScript beautified!');
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      toast.success('Copied!');
    }
  };

  return (
    <div className="space-y-4" data-testid="js-minifier-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">JavaScript Minifier</h3>
      </div>

      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="w-full">
          <TabsTrigger value="minify" className="flex-1">Minify</TabsTrigger>
          <TabsTrigger value="beautify" className="flex-1">Beautify</TabsTrigger>
        </TabsList>
      </Tabs>

      <Textarea
        placeholder="Paste your JavaScript here..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        className="min-h-[150px] bg-background/50 font-mono text-sm"
        data-testid="input"
      />

      <Button 
        onClick={mode === 'minify' ? minify : beautify} 
        className="w-full" 
        data-testid="action-btn"
      >
        {mode === 'minify' ? 'Minify JavaScript' : 'Beautify JavaScript'}
      </Button>

      {stats && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded bg-background/50 border border-border">
            <div className="text-lg font-bold">{stats.originalSize}</div>
            <div className="text-xs text-muted-foreground">Original</div>
          </div>
          <div className="p-2 rounded bg-background/50 border border-border">
            <div className="text-lg font-bold">{stats.minifiedSize}</div>
            <div className="text-xs text-muted-foreground">Minified</div>
          </div>
          <div className="p-2 rounded bg-emerald-500/20 border border-emerald-500/30">
            <div className="text-lg font-bold text-emerald-400">{stats.savings}%</div>
            <div className="text-xs text-muted-foreground">Saved</div>
          </div>
        </div>
      )}

      {output && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Output</span>
            <Button variant="ghost" size="sm" onClick={copyOutput}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>
          <Textarea
            value={output}
            readOnly
            className="min-h-[100px] bg-background/50 font-mono text-sm"
            data-testid="output"
          />
        </div>
      )}
    </div>
  );
};

export default JsMinifierTool;
